import {
  Badge,
  Button,
  Card,
  ChoiceList,
  DataTable,
  EmptyState,
  Layout,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris'
import { useCallback, useEffect, useMemo, useState } from 'react'

import FormattedDateTime from '../components/FormattedDateTime'
import { useAdminApi } from '../hooks/useAdminApi'

// Operator-managed promo codes applied to Shopify app subscriptions at subscribe time. Managed
// here (like the test-store allowlist); merchants redeem them on the app's Plans page. See
// docs/pricing.md "Launch discounts".
interface Discount {
  code: string
  description: string
  type: 'percentage' | 'fixed'
  value: number
  duration_intervals: number
  applies_to_plans: string[]
  applies_to_intervals: string[]
  max_redemptions: number
  redeemed_count: number
  active: boolean
  /** Applied without the merchant typing it — the public "launch offer". */
  auto_apply: boolean
  starts_at: string | null
  expires_at: string | null
  created_by: string
  created_at: string
  updated_at: string
  summary: string
}

const CODE_PATTERN = /^[A-Z0-9_-]{2,40}$/

// Empty form defaults — a brand-new code starts as an active, forever percentage discount
// that applies to all paid plans and billing periods.
const EMPTY_FORM = {
  code: '',
  description: '',
  type: 'percentage' as 'percentage' | 'fixed',
  value: '',
  duration_intervals: '0',
  planScope: 'all' as 'all' | 'specific',
  plans: [] as string[],
  intervalScope: 'all' as 'all' | 'specific',
  intervals: [] as string[],
  max_redemptions: '0',
  active: true,
  auto_apply: false,
  starts_at: '',
  expires_at: '',
}

type FormState = typeof EMPTY_FORM

// isoToDateInput turns an RFC3339 timestamp into the YYYY-MM-DD a <input type="date"> wants.
function isoToDateInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function scopeLabel(values: string[], allLabel: string): string {
  if (!values || values.length === 0) return allLabel
  return values.map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(', ')
}

export default function AdminDiscountsPage() {
  const { listDiscounts, createDiscount, updateDiscount, removeDiscount } =
    useAdminApi()

  const [discounts, setDiscounts] = useState<Discount[]>([])
  const [loading, setLoading] = useState(true)
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)

  // Create/edit modal. `editing` holds the code being edited (null = create).
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Delete + toggle
  const [removeTarget, setRemoveTarget] = useState<Discount | null>(null)
  const [removing, setRemoving] = useState(false)
  const [togglingCode, setTogglingCode] = useState<string | null>(null)

  const showToast = (message: string, isError = false) => {
    setToastMessage(message)
    setToastError(isError)
  }

  const fetchDiscounts = useCallback(async () => {
    setLoading(true)
    const response = await listDiscounts()
    if (response.success) {
      const data = response.data
      setDiscounts(Array.isArray(data) ? data : (data?.discounts ?? []))
    } else {
      showToast(response.error || 'Failed to load discounts', true)
    }
    setLoading(false)
  }, [listDiscounts])

  useEffect(() => {
    fetchDiscounts()
  }, [fetchDiscounts])

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
    if (formError) setFormError('')
  }

  const openCreate = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormError('')
    setModalOpen(true)
  }

  const openEdit = (d: Discount) => {
    setEditing(d.code)
    setForm({
      code: d.code,
      description: d.description,
      type: d.type,
      value: String(d.value),
      duration_intervals: String(d.duration_intervals),
      planScope: (d.applies_to_plans ?? []).length > 0 ? 'specific' : 'all',
      plans: d.applies_to_plans ?? [],
      intervalScope:
        (d.applies_to_intervals ?? []).length > 0 ? 'specific' : 'all',
      intervals: d.applies_to_intervals ?? [],
      max_redemptions: String(d.max_redemptions),
      active: d.active,
      auto_apply: d.auto_apply === true,
      starts_at: isoToDateInput(d.starts_at),
      expires_at: isoToDateInput(d.expires_at),
    })
    setFormError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    if (submitting) return
    setModalOpen(false)
  }

  // buildPayload validates the form and returns the request body, or null (+ inline error).
  const buildPayload = (): Record<string, unknown> | null => {
    const code = form.code.trim().toUpperCase()
    if (!CODE_PATTERN.test(code)) {
      setFormError('Code must be 2–40 characters using A–Z, 0–9, _ or -.')
      return null
    }
    const value = Number(form.value)
    if (!Number.isFinite(value) || value <= 0) {
      setFormError('Enter a value greater than 0.')
      return null
    }
    if (form.type === 'percentage' && value > 100) {
      setFormError('Percentage must be between 1 and 100.')
      return null
    }
    const duration = Number(form.duration_intervals)
    if (!Number.isInteger(duration) || duration < 0 || duration > 12) {
      setFormError(
        'Duration must be a whole number between 0 (forever) and 12.'
      )
      return null
    }
    // Resolve the All/Specific scopes to the arrays the backend expects (empty = all).
    const plans = form.planScope === 'all' ? [] : form.plans
    const intervals = form.intervalScope === 'all' ? [] : form.intervals
    if (form.planScope === 'specific' && plans.length === 0) {
      setFormError('Pick at least one plan, or choose "All paid plans".')
      return null
    }
    if (form.intervalScope === 'specific' && intervals.length === 0) {
      setFormError(
        'Pick at least one billing period, or choose "All billing periods".'
      )
      return null
    }
    // durationLimitInIntervals is denominated in the billing interval, so a time-limited code
    // must target exactly one interval (mirrors the backend rule).
    if (duration > 0 && intervals.length !== 1) {
      setFormError(
        'A time-limited discount must apply to exactly one billing period. Choose "Specific period" and pick one.'
      )
      return null
    }
    const max = Number(form.max_redemptions)
    if (!Number.isInteger(max) || max < 0) {
      setFormError(
        'Max redemptions must be 0 (unlimited) or a positive whole number.'
      )
      return null
    }
    if (
      form.starts_at &&
      form.expires_at &&
      form.starts_at >= form.expires_at
    ) {
      setFormError('The start date must be before the expiry date.')
      return null
    }

    return {
      code,
      description: form.description.trim(),
      type: form.type,
      value,
      duration_intervals: duration,
      applies_to_plans: plans,
      applies_to_intervals: intervals,
      max_redemptions: max,
      active: form.active,
      auto_apply: form.auto_apply,
      // Send an inclusive window: start of the start day, end of the expiry day.
      starts_at: form.starts_at ? `${form.starts_at}T00:00:00Z` : null,
      expires_at: form.expires_at ? `${form.expires_at}T23:59:59Z` : null,
    }
  }

  const handleSubmit = async () => {
    const payload = buildPayload()
    if (!payload) return

    setSubmitting(true)
    const response = editing
      ? await updateDiscount(editing, payload)
      : await createDiscount(payload)
    setSubmitting(false)

    if (response.success) {
      showToast(editing ? 'Discount updated' : `Created ${payload.code}`)
      setModalOpen(false)
      fetchDiscounts()
    } else {
      setFormError(response.error || 'Failed to save discount')
    }
  }

  const handleToggleActive = async (d: Discount) => {
    setTogglingCode(d.code)
    // A PUT replaces the editable fields, so resend the whole row with `active` flipped —
    // redeemed_count and created_by are preserved server-side.
    const response = await updateDiscount(d.code, {
      code: d.code,
      description: d.description,
      type: d.type,
      value: d.value,
      duration_intervals: d.duration_intervals,
      applies_to_plans: d.applies_to_plans ?? [],
      applies_to_intervals: d.applies_to_intervals ?? [],
      max_redemptions: d.max_redemptions,
      active: !d.active,
      // Resend auto_apply verbatim — the backend reads an omitted field as FALSE (it is opt-in),
      // so leaving it out would quietly end the launch offer on an unrelated Activate click.
      auto_apply: d.auto_apply === true,
      starts_at: d.starts_at,
      expires_at: d.expires_at,
    })
    setTogglingCode(null)
    if (response.success) {
      showToast(d.active ? `${d.code} deactivated` : `${d.code} activated`)
      fetchDiscounts()
    } else {
      showToast(response.error || 'Failed to update discount', true)
    }
  }

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return
    setRemoving(true)
    const response = await removeDiscount(removeTarget.code)
    setRemoving(false)
    if (response.success) {
      showToast(`Removed ${removeTarget.code}`)
      setRemoveTarget(null)
      fetchDiscounts()
    } else {
      showToast(response.error || 'Failed to remove discount', true)
    }
  }

  const statusBadge = (d: Discount) => {
    if (!d.active) return <Badge tone="new">Inactive</Badge>
    if (d.expires_at && new Date(d.expires_at).getTime() < Date.now()) {
      return <Badge tone="warning">Expired</Badge>
    }
    return <Badge tone="success">Active</Badge>
  }

  const rows = useMemo(
    () =>
      discounts.map(d => [
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {d.code}
        </Text>,
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Text as="span" variant="bodyMd">
            {d.summary}
          </Text>
          {/* An auto-applied code reaches EVERY merchant, so it is worth seeing from the list —
              the difference between a public promotion and a code handed to one shop. */}
          {d.auto_apply ? <Badge tone="info">Auto-applied</Badge> : null}
        </div>,
        `${scopeLabel(d.applies_to_plans, 'All plans')} · ${scopeLabel(
          d.applies_to_intervals,
          'Any period'
        )}`,
        d.max_redemptions > 0
          ? `${d.redeemed_count} / ${d.max_redemptions}`
          : String(d.redeemed_count),
        statusBadge(d),
        d.expires_at ? <FormattedDateTime date={d.expires_at} /> : '—',
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button size="micro" onClick={() => openEdit(d)}>
            Edit
          </Button>
          <Button
            size="micro"
            loading={togglingCode === d.code}
            onClick={() => handleToggleActive(d)}
          >
            {d.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            size="micro"
            tone="critical"
            onClick={() => setRemoveTarget(d)}
          >
            Delete
          </Button>
        </div>,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discounts, togglingCode]
  )

  const toast = toastMessage ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastMessage('')}
    />
  ) : null

  return (
    <>
      {toast}
      <Page
        title="Discounts"
        subtitle="Promo codes merchants can apply on the Plans page at checkout."
        primaryAction={{ content: 'Create code', onAction: openCreate }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : discounts.length === 0 ? (
                <EmptyState
                  heading="No discount codes yet"
                  image=""
                  action={{ content: 'Create code', onAction: openCreate }}
                >
                  <p>
                    Create a promo code (e.g. a Product Hunt launch code) that
                    merchants can redeem on the app&apos;s Plans page.
                  </p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Code',
                    'Offer',
                    'Applies to',
                    'Redeemed',
                    'Status',
                    'Expires',
                    'Actions',
                  ]}
                  rows={rows}
                />
              )}
            </Card>
          </Layout.Section>
        </Layout>

        {/* Create / edit modal */}
        <Modal
          open={modalOpen}
          onClose={closeModal}
          title={editing ? `Edit ${editing}` : 'Create discount code'}
          primaryAction={{
            content: editing ? 'Save changes' : 'Create code',
            onAction: handleSubmit,
            loading: submitting,
          }}
          secondaryActions={[
            { content: 'Cancel', onAction: closeModal, disabled: submitting },
          ]}
        >
          <Modal.Section>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              {formError ? (
                <Text as="p" tone="critical">
                  {formError}
                </Text>
              ) : null}

              <TextField
                label="Code"
                value={form.code}
                onChange={v => setField('code', v.toUpperCase())}
                autoComplete="off"
                disabled={editing !== null}
                placeholder="PRODUCTHUNT"
                helpText="A–Z, 0–9, _ or -. Merchants type this at checkout."
              />
              <TextField
                label="Description (internal)"
                value={form.description}
                onChange={v => setField('description', v)}
                autoComplete="off"
                placeholder="Product Hunt launch — 2 months free"
              />

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    label="Type"
                    options={[
                      { label: 'Percentage off', value: 'percentage' },
                      { label: 'Fixed amount off', value: 'fixed' },
                    ]}
                    value={form.type}
                    onChange={v =>
                      setField('type', v as 'percentage' | 'fixed')
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Value"
                    type="number"
                    value={form.value}
                    onChange={v => setField('value', v)}
                    autoComplete="off"
                    prefix={form.type === 'fixed' ? '$' : undefined}
                    suffix={form.type === 'percentage' ? '%' : undefined}
                    helpText={
                      form.type === 'percentage'
                        ? '100 = free'
                        : 'USD off per period'
                    }
                  />
                </div>
              </div>

              <TextField
                label="Duration (billing periods)"
                type="number"
                value={form.duration_intervals}
                onChange={v => setField('duration_intervals', v)}
                autoComplete="off"
                helpText="How many periods the discount lasts. 0 = forever. A time-limited code must target one billing period below."
              />

              <div>
                <ChoiceList
                  title="Applies to plans"
                  choices={[
                    {
                      label: 'All paid plans (Starter & Growth)',
                      value: 'all',
                    },
                    { label: 'Specific plans', value: 'specific' },
                  ]}
                  selected={[form.planScope]}
                  onChange={v =>
                    setField('planScope', (v[0] as 'all' | 'specific') ?? 'all')
                  }
                />
                {form.planScope === 'specific' ? (
                  <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                    <ChoiceList
                      allowMultiple
                      titleHidden
                      title="Plans"
                      choices={[
                        { label: 'Starter', value: 'starter' },
                        { label: 'Growth', value: 'growth' },
                      ]}
                      selected={form.plans}
                      onChange={v => setField('plans', v)}
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <ChoiceList
                  title="Applies to billing periods"
                  choices={[
                    {
                      label: 'All billing periods (Monthly & Annual)',
                      value: 'all',
                    },
                    { label: 'Specific period', value: 'specific' },
                  ]}
                  selected={[form.intervalScope]}
                  onChange={v =>
                    setField(
                      'intervalScope',
                      (v[0] as 'all' | 'specific') ?? 'all'
                    )
                  }
                />
                {form.intervalScope === 'specific' ? (
                  <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                    <ChoiceList
                      allowMultiple
                      titleHidden
                      title="Billing periods"
                      choices={[
                        { label: 'Monthly', value: 'monthly' },
                        { label: 'Annual', value: 'annual' },
                      ]}
                      selected={form.intervals}
                      onChange={v => setField('intervals', v)}
                    />
                  </div>
                ) : null}
                {Number(form.duration_intervals) > 0 ? (
                  <div style={{ marginTop: '4px' }}>
                    <Text as="p" tone="subdued" variant="bodySm">
                      A time-limited discount must target exactly one billing
                      period (2 periods = 2 months on monthly, but 2 years on
                      annual).
                    </Text>
                  </div>
                ) : null}
              </div>

              <TextField
                label="Max redemptions"
                type="number"
                value={form.max_redemptions}
                onChange={v => setField('max_redemptions', v)}
                autoComplete="off"
                helpText="0 = unlimited."
              />

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Starts (optional)"
                    type="date"
                    value={form.starts_at}
                    onChange={v => setField('starts_at', v)}
                    autoComplete="off"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Expires (optional)"
                    type="date"
                    value={form.expires_at}
                    onChange={v => setField('expires_at', v)}
                    autoComplete="off"
                  />
                </div>
              </div>

              <ChoiceList
                title="Status"
                choices={[
                  { label: 'Active (merchants can redeem)', value: 'active' },
                ]}
                selected={form.active ? ['active'] : []}
                onChange={v => setField('active', v.includes('active'))}
              />

              <ChoiceList
                title="Launch offer"
                choices={[
                  {
                    label: 'Apply automatically — no code to type',
                    value: 'auto',
                    helpText:
                      'Every merchant who opens Plans gets this offer, already applied. Use it for a public promotion the App Store listing advertises; leave it off for a code you hand to one shop. Untick it to end the promotion — shops that already subscribed keep their discount.',
                  },
                ]}
                selected={form.auto_apply ? ['auto'] : []}
                onChange={v => setField('auto_apply', v.includes('auto'))}
              />
            </div>
          </Modal.Section>
        </Modal>

        {/* Delete confirmation */}
        <Modal
          open={removeTarget !== null}
          onClose={() => (removing ? undefined : setRemoveTarget(null))}
          title="Delete discount code"
          primaryAction={{
            content: 'Delete',
            destructive: true,
            onAction: handleRemoveConfirm,
            loading: removing,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setRemoveTarget(null),
              disabled: removing,
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Delete{' '}
              <Text as="span" fontWeight="semibold">
                {removeTarget?.code}
              </Text>
              ? Merchants will no longer be able to redeem it. Existing
              subscriptions keep the discount they were created with.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  )
}
